import {
  defaultEventCategorySet,
  TaskItem,
  TaskItemDuration,
  TaskItemForSchedule,
  TaskLabel,
} from '@demind-inc/core';
import { DEFAULT_TASK_DURATION_MIN } from '../../../../data-access';
import { getDueDateYYYYMMDD, getIsDueDateWithinXDaysBefore } from '../../../../helpers';

export interface TaskItemForScheduleWithSelected extends TaskItemForSchedule {
  selected?: boolean;
  aiNote?: string;
}
export interface TasksPrefItemState {
  [taskId: string]: TaskItemForScheduleWithSelected;
}
export interface TaskItemWithSelected extends TaskItem {
  selected: boolean;
}

export const tasksPrefItemReducer = (
  state: TasksPrefItemState,
  action: {
    type: 'update' | 'initialize' | 'check' | 'uncheck';
    taskId?: string;
    payload: Partial<TaskItemForScheduleWithSelected> | TasksPrefItemState;
  }
): TasksPrefItemState => {
  if (action.type === 'initialize') {
    return action.payload as TasksPrefItemState;
  }

  if (!action.taskId) {
    return state;
  }

  return {
    ...state,
    [action.taskId]: {
      ...state[action.taskId],
      ...(action.payload as Partial<TaskItemForScheduleWithSelected>),
    },
  };
};

export const transformTodosToTaskPref = (
  tasks: TaskItem[],
  selectedDate?: string,
  previousState?: TasksPrefItemState
): TasksPrefItemState => {
  const defaultTaskDuration: TaskItemDuration = {
    amount: DEFAULT_TASK_DURATION_MIN,
    unit: 'minute',
  };

  return tasks.reduce((acc, task) => {
    const isDueWithin2Days = getIsDueDateWithinXDaysBefore(2, task.dueDateTime, selectedDate);
    const { canTaskBeScheduled, hasScheduledAlready } = selectedDate
      ? getTaskToBeScheduled(task, selectedDate)
      : { canTaskBeScheduled: true, hasScheduledAlready: false };

    const previousTask = previousState?.[task.taskId];
    const isPreviouslySelected =
      typeof previousTask?.selected === 'boolean' ? previousTask.selected : undefined;
    const defaultSelected = hasScheduledAlready ? false : isDueWithin2Days && canTaskBeScheduled;
    const selected = canTaskBeScheduled ? (isPreviouslySelected ?? defaultSelected) : false;

    acc[task.taskId] = {
      taskId: task.taskId,
      name: task.name,
      appFrom: task.appFrom,
      boardId: task.boardId,
      duration: task.duration
        ? task.duration.amount > 0
          ? task.duration
          : defaultTaskDuration
        : defaultTaskDuration,
      sortIndex: task.sortIndex,
      labels: task.labels,
      addedToCalendar: task.addedToCalendar,
      dueDateTime: task.dueDateTime,
      aiNote: task.aiNote,
      selected,
      priority: task.priority,
    };
    return acc;
  }, {} as TasksPrefItemState);
};

const labelsToBeAccepcted = defaultEventCategorySet
  .filter((item) => ['default_deepWork', 'default_lightTask'].includes(item.id))
  .map(({ name }) => name);

export const getTaskToBeScheduled = (task: TaskItemForScheduleWithSelected, targetDate: string) => {
  const duration = task.duration?.unit === 'minute' ? task.duration?.amount : undefined;
  const hasScheduledAlready =
    task.addedToCalendar && getDueDateYYYYMMDD(task.dueDateTime) === targetDate;

  return {
    duration,
    canTaskBeScheduled: true,
    disabledReason: hasScheduledAlready ? 'Task is already scheduled to calendar' : '',
    hasScheduledAlready,
  };
};

/**
 * Sorts tasks based on whether they can be scheduled and their sort index
 * @param tasks - The tasks to sort
 * @returns Sorted array of task entries
 */
export const sortTasks = (
  tasks: [string, TaskItemForScheduleWithSelected][],
  targetDate: string
) => {
  return [...tasks].sort((a, b) => {
    const taskA = a[1];
    const taskB = b[1];

    const { canTaskBeScheduled: canScheduleA } = getTaskToBeScheduled(taskA, targetDate);
    const { canTaskBeScheduled: canScheduleB } = getTaskToBeScheduled(taskB, targetDate);

    // First sort by canTaskBeScheduled (true values first)
    if (canScheduleA && !canScheduleB) return -1;
    if (!canScheduleA && canScheduleB) return 1;

    // Then sort by sortIndex (lower values first)
    const sortIndexA = taskA.sortIndex || Number.MAX_SAFE_INTEGER;
    const sortIndexB = taskB.sortIndex || Number.MAX_SAFE_INTEGER;

    return sortIndexA - sortIndexB;
  });
};

export const transformTaskPrefForEndpoint = (
  tasks: TasksPrefItemState,
  targetDate: string
): Array<TaskItemForScheduleWithSelected> => {
  return Object.values(tasks)
    .map((task) => task)
    .filter(
      (task) =>
        getTaskToBeScheduled(task, targetDate).canTaskBeScheduled &&
        task.selected &&
        task.selected === true
    );
};

export const getAcceptedLabel = (labels?: TaskLabel[]) => {
  return labels?.find((label) => labelsToBeAccepcted.some((prefix) => label.name.includes(prefix)));
};
